
import React from 'react';
import { Order, OrderStatus } from '../types';
import { StatusBadge } from './ui';
import { Clock, MoreVertical, ArrowRight, Truck, Check, CheckCircle } from 'lucide-react';

interface KanbanBoardProps {
  orders: Order[];
  onStatusChange: (orderId: string, newStatus: OrderStatus) => void;
  onOrderClick?: (order: Order) => void;
}

export const KanbanBoard = ({ orders, onStatusChange, onOrderClick }: KanbanBoardProps) => {
  const columns = [
    { title: 'New & Design', status: [OrderStatus.PENDING, OrderStatus.DESIGNING, OrderStatus.NEEDS_REVIEW], color: 'bg-blue-500' },
    { title: 'Production', status: [OrderStatus.MILLING, OrderStatus.READY], color: 'bg-purple-500' },
    { title: 'Logistics', status: [OrderStatus.OUT_FOR_DELIVERY], color: 'bg-cyan-500' },
    { title: 'Try-In / Clinic', status: [OrderStatus.IN_TRY_IN], color: 'bg-indigo-500' },
  ];

  return (
    <div className="flex gap-4 md:gap-6 overflow-x-auto pb-6 min-h-[500px] snap-x snap-mandatory scroll-smooth -mx-4 px-4 md:mx-0 md:px-0">
      {columns.map((col, idx) => (
        <div key={idx} className="min-w-[85vw] md:min-w-[320px] bg-zinc-900 border border-zinc-800 rounded-xl p-4 flex flex-col gap-4 snap-center h-full">
          <div className="flex justify-between items-center pb-3 border-b border-zinc-800">
            <div className="flex items-center gap-3">
              <div className={`w-2.5 h-2.5 rounded-full ${col.color} shadow-[0_0_8px_currentColor]`}></div>
              <h3 className="font-bold text-white text-sm tracking-wide">{col.title}</h3>
            </div>
            <span className="bg-zinc-800 border border-zinc-700 px-2 py-0.5 rounded text-xs font-bold text-zinc-400">
              {orders.filter(o => col.status.includes(o.status)).length}
            </span>
          </div>

          <div className="flex-1 space-y-3 overflow-y-auto max-h-[600px] pr-2 custom-scrollbar">
            {orders.filter(o => col.status.includes(o.status)).map(order => (
              <div
                key={order.id}
                onClick={() => onOrderClick && onOrderClick(order)}
                className="bg-zinc-950 p-5 rounded-xl border border-zinc-800 hover:border-zinc-600 hover:shadow-lg transition-all group cursor-pointer relative overflow-hidden active:scale-[0.98] duration-200"
              >
                {/* Accent Line */}
                <div className={`absolute left-0 top-0 bottom-0 w-1 ${col.color} opacity-0 group-hover:opacity-100 transition-opacity`}></div>

                <div className="flex justify-between items-start mb-3">
                  <span className="font-bold text-white text-lg tracking-tight">#{order.toothNumber}</span>
                  <span className="text-[10px] font-mono text-zinc-500 bg-zinc-900 border border-zinc-800 px-1.5 py-0.5 rounded">#{order.id.slice(-4)}</span>
                </div>

                <h4 className="font-medium text-zinc-200 mb-1 truncate">{order.patientName}</h4>
                <div className="flex justify-between items-center mb-4">
                  <span className="text-xs text-zinc-500 uppercase tracking-wider font-bold truncate max-w-[120px]">
                    {order.items && order.items.length > 0
                      ? order.items[0].serviceName
                      : 'Standard'}
                  </span>
                  <StatusBadge status={order.status} />
                </div>

                <div className="flex items-center gap-2 text-xs text-zinc-500 border-t border-zinc-900 pt-3">
                  <Clock size={12} />
                  <span>Due: {new Date(order.dueDate).toLocaleDateString()}</span>
                </div>

                {/* Simplified Advance Logic for Kanban */}
                <div className="absolute right-4 bottom-4 opacity-0 group-hover:opacity-100 transition-all transform translate-x-2 group-hover:translate-x-0 flex gap-2">
                  {order.status === OrderStatus.MILLING && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onStatusChange(order.id, OrderStatus.OUT_FOR_DELIVERY); }}
                      className="w-8 h-8 rounded-full bg-cyan-500 text-black flex items-center justify-center hover:bg-cyan-400 shadow-lg transition-colors"
                      title="Send for Delivery"
                    >
                      <Truck size={14} />
                    </button>
                  )}
                  {order.status === OrderStatus.OUT_FOR_DELIVERY && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onStatusChange(order.id, OrderStatus.DELIVERED); }}
                      className="w-8 h-8 rounded-full bg-white text-black flex items-center justify-center hover:bg-zinc-200 shadow-lg transition-colors"
                      title="Mark Delivered"
                    >
                      <ArrowRight size={14} />
                    </button>
                  )}
                  {order.status === OrderStatus.IN_TRY_IN && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onStatusChange(order.id, OrderStatus.DELIVERED); }}
                      className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center hover:bg-green-600 shadow-lg transition-colors"
                      title="Mark Final Delivery"
                    >
                      <CheckCircle size={14} />
                    </button>
                  )}
                </div>
              </div>
            ))}

            {orders.filter(o => col.status.includes(o.status)).length === 0 && (
              <div className="h-24 border-2 border-dashed border-zinc-800 rounded-xl flex items-center justify-center">
                <span className="text-zinc-600 text-sm font-medium">Empty</span>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
